import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import ReportItemADA from "Reporting/Components/ReportItemADA";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";

/*
 * Table displaying different items in a report with the functionality to sort and filter
 */
export default function ReportTableADA({
  report,
  filters,
  onSortedReportsChange,
  selectedColumns,
  onSelectedColumnsChange,
}) {
  const PAGE_HEIGHT_PX = 425;
  const tableRef = useRef(null);

  const [pageBreakIndexes, setPageBreakIndexes] = useState([]);
  const [sort, setSort] = useState([]);

  const [sortedReports, setSortedReports] = useState(report.adaData || []);
  const [totalItem, setTotalItem] = useState({
    IBO: "Total",
    lastName: "",
    displayName: "",
    pin: "",
    diamond: "",
    attendingEvent: "",
    adaName: "",
    contact: "",
    columns: [],
  });

  const allColumns = useMemo(() => {
    const seen = new Set();
    const grouped = [];

    const sortedHeaders = [...report.headers].sort(
      (a, b) => a.columnHeaderSort - b.columnHeaderSort
    );
    sortedHeaders.forEach((header) => {
      const matchingColumns = (report.adaData || [])
        .flatMap((item) => item.columns)
        .filter((col) => col.columnHeaderValue === header.columnHeaderValue);

      const uniqueColumns = Array.from(
        new Map(matchingColumns.map((col) => [col.columnName, col])).values()
      );
      // Sort by columnSort
      uniqueColumns.sort((a, b) => Number(a.columnSort) - Number(b.columnSort));
      uniqueColumns.forEach((col) => {
        if (!seen.has(col.columnName)) {
          seen.add(col.columnName);
          grouped.push(col);
        }
      });
    });
    return grouped;
  }, [report]);

  const groupedHeaders = useMemo(() => {
    const sortedHeaders = [...report.headers].sort(
      (a, b) => a.columnHeaderSort - b.columnHeaderSort
    );
    return sortedHeaders.map((header) => {
      const matchingColumns = (report.adaData || [])
        .flatMap((item) => item.columns)
        .filter((col) => col.columnHeaderValue === header.columnHeaderValue);
      const uniqueColumns = Array.from(
        new Map(matchingColumns.map((col) => [col.columnName, col])).values()
      );

      uniqueColumns.sort((a, b) => Number(a.columnSort) - Number(b.columnSort));
      return {
        ...header,
        columns: uniqueColumns,
        colSpan: uniqueColumns.length,
      };
    });
  }, [report, allColumns]);

  useEffect(() => {
    const rows = sortedReports || [];

    const newTotal = {
      IBO: "Total",
      lastName: "",
      displayName: "",
      pin: "",
      diamond: "",
      attendingEvent: "",
      adaName: "",
      contact: "",
      columns: allColumns.map((col) => {
        let total = 0;

        rows.forEach((r) => {
          const match = (r.columns || []).find(
            (c) => c.columnName === col.columnName
          );

          if (match && match.columnValue) {
            total += Number(match.columnValue);
          }
        });

        return {
          ...col,
          columnValue: total,
        };
      }),
    };

    setTotalItem(newTotal);
  }, [sortedReports, allColumns]);

  const prevColumnKeyRef = useRef("");

  useEffect(() => {
    if (!allColumns.length) return;

    const columnKey = allColumns.map((c) => c.columnName).join("|");

    if (columnKey !== prevColumnKeyRef.current) {
      onSelectedColumnsChange?.(allColumns.map((c) => c.columnName));
      setSort([]);
      prevColumnKeyRef.current = columnKey;
    }
  }, [allColumns, onSelectedColumnsChange]);

  useEffect(() => {
    let result = [...report.adaData];

    result = result.filter((row) => {
      return selectedColumns.some((colName) => {
        const match = row.columns.find((c) => c.columnName === colName);
        return match && match.columnValue;
      });
    });

    if (sort.length > 0) {
      result.sort((a, b) => {
        for (const s of sort) {
          if (!s.field || s.direction === "none") continue;

          let aVal, bVal;

          switch (s.field) {
            case "IBO":
              aVal = a.IBO;
              bVal = b.IBO;
              break;
            case "Last Name":
              aVal = a.lastName;
              bVal = b.lastName;
              break;
            case "PIN":
              aVal = a.pin;
              bVal = b.pin;
              break;
            case "Diamond":
              aVal = a.diamond;
              bVal = b.diamond;
              break;
            default:
              aVal =
                a.columns.find((col) => col.columnName === s.field)
                  ?.columnValue ?? 0;
              bVal =
                b.columns.find((col) => col.columnName === s.field)
                  ?.columnValue ?? 0;
          }

          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();

          if ((aVal === 0 || aVal === 1) && (bVal === 0 || bVal === 1)) {
            if (aVal !== bVal) {
              return s.direction === "asc" ? bVal - aVal : aVal - bVal;
            }
            continue;
          }

          if (aVal < bVal) return s.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return s.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setSortedReports(result);
    onSortedReportsChange?.(result, selectedColumns);
  }, [report.adaData, sort, selectedColumns]);

  const toggleSort = (field) => {
    setSort((prev) => {
      const existingIndex = prev.findIndex((s) => s.field === field);
      let newSort;

      if (existingIndex === -1) {
        newSort = [...prev, { field, direction: "asc" }];
      } else {
        const existing = prev[existingIndex];
        let nextDirection;
        if (existing.direction === "asc") nextDirection = "desc";
        else if (existing.direction === "desc") nextDirection = "none";
        else nextDirection = "asc";

        if (nextDirection === "none") {
          newSort = prev.filter((_, i) => i !== existingIndex);
        } else {
          newSort = [...prev];
          newSort[existingIndex] = { field, direction: nextDirection };
        }
      }

      return newSort;
    });
  };

  useEffect(() => {
    setSort((prev) =>
      prev.filter(
        (s) =>
          !s.field ||
          ["IBO", "Last Name", "PIN", "Diamond"].includes(s.field) ||
          selectedColumns.includes(s.field)
      )
    );
  }, [selectedColumns]);

  const renderSortIcon = (field) => {
    const index = sort.findIndex((s) => s.field === field);
    if (index === -1 || sort[index].direction === "none") {
      return <FontAwesomeIcon icon={faMinus} className={styles.menuChevron} />;
    }

    const icon =
      sort[index].direction === "asc"
        ? faChevronUp
        : sort[index].direction === "desc"
        ? faChevronDown
        : faMinus;

    return (
      <div className={styles.sortWrapper}>
        <FontAwesomeIcon icon={icon} className={styles.menuChevron} />
        <span className={styles.sortPriority}>{index + 1}</span>
      </div>
    );
  };

  // Calculate page breaks for PDF/export, using a single ref
  const calculateBreaks = (ref, rowCount, setBreaks) => {
    if (!ref.current) {
      console.warn("Ref not available for break calculation");
      return;
    }

    const timeout = setTimeout(() => {
      const allElements = Array.from(
        ref.current.querySelectorAll(`.${styles.pageRowWrapper}`)
      );

      const newBreaks = [];
      // Get both the header and the title (headerInfo) heights
      const headerEl = ref.current.querySelector(`.${styles.header}`);
      const headerHeight = headerEl?.getBoundingClientRect().height || 0;
      // headerInfo is outside the tableRef, so get it from the parent
      let headerInfoHeight = 0;
      const parent = ref.current.parentElement;
      if (parent) {
        const headerInfoEl = parent.querySelector(`.${styles.headerInfo}`);
        if (headerInfoEl)
          headerInfoHeight = headerInfoEl.getBoundingClientRect().height;
      }

      let staticHeight = headerInfoHeight + headerHeight;
      // If headerInfoHeight is 0, manually add 50px for the title/header
      if (headerInfoHeight === 0) {
        staticHeight += 50;
        // console.log(
        //   "[PageBreak] headerInfoHeight missing, adding 50px manually"
        // );
      }
      // console.log(
      //   "[PageBreak] headerHeight:",
      //   headerHeight,
      //   "headerInfoHeight:",
      //   headerInfoHeight,
      //   "staticHeight:",
      //   staticHeight
      // );

      let heightSoFar = 0;
      let currentRowIndex = 0;
      let isFirstPage = true;

      allElements.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        // console.log(`[PageBreak] Row ${i} height:`, rect.height);
        if (isFirstPage) {
          heightSoFar += staticHeight;
          isFirstPage = false;
        }
        // Check if adding this row would exceed the page height
        if (heightSoFar + rect.height > PAGE_HEIGHT_PX) {
          newBreaks.push(currentRowIndex);
          // console.log(
          //   `[PageBreak] --- PAGE BREAK at row ${currentRowIndex}, resetting heightSoFar to row height:`,
          //   rect.height
          // );
          heightSoFar = rect.height;
        } else {
          heightSoFar += rect.height;
        }
        // console.log(`[PageBreak] After row ${i}, heightSoFar:`, heightSoFar);
        currentRowIndex++;
      });

      setBreaks(newBreaks);
    }, 100);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    return calculateBreaks(
      tableRef,
      sortedReports.length + (totalItem ? 1 : 0),
      setPageBreakIndexes
    );
  }, [sortedReports, totalItem]);

  return (
    <div>
      {/* {index !== 0 && <div className={styles.pageBreak} />} */}
      <div className={styles.headerInfo}>
        <div className={styles.title}>
          <div className={styles.event}>{report.reportTitle}</div>
        </div>
      </div>

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={tableRef}>
          <div className={styles.upperHeader}>
            <div
              className={styles.upperHeaderFiller}
              style={{ flex: `0 0 70%`, maxWidth: `70%` }}
            />
            {groupedHeaders.map((header) => {
              const headerColCount = header.columns.length;
              const totalColCount = allColumns.length;
              const widthPercent =
                totalColCount > 0 ? (headerColCount / totalColCount) * 30 : 0;

              return (
                <div
                  key={header.columnHeaderValue}
                  className={styles.headerGroup}
                  style={{
                    flex: `0 0 ${widthPercent}%`,
                    maxWidth: `${widthPercent}%`,
                  }}
                >
                  <input
                    type="checkbox"
                    className={styles.headerGroupCheckbox}
                    checked={header.columns.every((col) =>
                      selectedColumns.includes(col.columnName)
                    )}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const newSelected = [...selectedColumns];

                      header.columns.forEach((col) => {
                        if (checked && !newSelected.includes(col.columnName)) {
                          newSelected.push(col.columnName);
                        } else if (!checked) {
                          const index = newSelected.indexOf(col.columnName);
                          if (index > -1) newSelected.splice(index, 1);
                        }
                      });

                      onSelectedColumnsChange?.(newSelected);
                    }}
                  />

                  <img
                    src={
                      header.columnHeaderIcon.startsWith("data:image")
                        ? header.columnHeaderIcon
                        : header.columnHeaderIcon.startsWith("http")
                        ? header.columnHeaderIcon
                        : `data:image/png;base64,${header.columnHeaderIcon}`
                    }
                    alt=""
                    className={styles.headerGroupIcon}
                  />
                </div>
              );
            })}
          </div>
          <div className={styles.header}>
            <div className={styles.ibo} onClick={() => toggleSort("IBO")}>
              IBO{renderSortIcon("IBO")}
            </div>
            <div
              className={styles.lastName}
              onClick={() => toggleSort("Last Name")}
            >
              Last Name{renderSortIcon("Last Name")}
            </div>
            <div className={styles.displayName}>IBOs</div>
            <div className={styles.pin} onClick={() => toggleSort("PIN")}>
              PIN{renderSortIcon("PIN")}
            </div>
            <div
              className={styles.diamond}
              onClick={() => toggleSort("Diamond")}
            >
              Diamond{renderSortIcon("Diamond")}
            </div>
            <div className={styles.attendingEvent}>Attending Event</div>
            <div className={styles.adaName}>ADA Name</div>
            <div className={styles.contact}>Contact</div>
            {allColumns.map((col, index) => {
              const isSelected = selectedColumns.includes(col.columnName);

              return (
                <div
                  key={col.columnName}
                  className={index % 2 !== 0 ? styles.col : styles.oddCol}
                  style={{
                    flex: `1 1 0`,
                    minWidth: 0,
                    width: `${30 / allColumns.length}%`,
                    cursor: isSelected ? "pointer" : "default",
                    opacity: isSelected ? 1 : 0.4,
                  }}
                  onClick={
                    isSelected ? () => toggleSort(col.columnName) : undefined
                  }
                >
                  {isSelected && renderSortIcon(col.columnName)}
                  {col.columnName}
                </div>
              );
            })}
          </div>

          {totalItem && (
            <React.Fragment key="total">
              {pageBreakIndexes.includes(sortedReports.length) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemADA
                  item={totalItem}
                  isOdd={true}
                  type="total"
                  allColumns={allColumns}
                  selectedColumns={selectedColumns}
                />
              </div>
            </React.Fragment>
          )}

          {sortedReports.map((row, i) => (
            <React.Fragment key={i}>
              {pageBreakIndexes.includes(i) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemADA
                  item={row}
                  isOdd={i % 2 !== 0}
                  type="row"
                  allColumns={allColumns}
                  selectedColumns={selectedColumns}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

ReportTableADA.propTypes = {
  item: PropTypes.shape({
    eventOwner: PropTypes.string,
    eventType: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    events: PropTypes.arrayOf(PropTypes.object),
  }),
};
